import * as THREE from 'three';
import { carPartOf } from '@/entities/carLook';

/**
 * modSurface.ts (M128c, M153) — the ride's REAL shape, measured from the
 * fitted GLB, in car-local units. Three silhouettes:
 *
 *   top   — the highest point over each (x, z) cell (seen from above): what
 *           the cape lies on and the hair floors on, where a rally pod's feet,
 *           a glove's cuff and a hood ornament stand;
 *   rear  — the rearmost point behind each (x, y) cell (seen from behind):
 *           where the cape HANGS clear of the tail, where a tail decal sits;
 *   side  — the outermost |x| beside each (z, y) cell (seen from the side):
 *           how wide the body really is at a sill, a haunch, a wheel arch.
 *
 * M153: the faces are RASTERISED (points every few centimetres over every
 * triangle), not just their corners. The M128c field binned vertices only, so
 * a cell under a big flat roof or engine-cover triangle held nothing but the
 * floor pan beneath it — and the cape dropped through the bodywork there
 * (the torn capes on the new fleet). The measurement is LAZY: CarView measures
 * every car the moment its model lands, and only a ride wearing mods ever
 * reads the field.
 *
 * Wheels, mirrors, axles, coils and the like are skipped by name so hair
 * never floors on a tyre and a sill plate never measures a wheel.
 *
 * M154: each silhouette has a twin holding only the NON-paint geometry (a
 * hood vent's well and slats, a side scoop's mouth, a grille, a lamp, glass,
 * a carbon blade, a diffuser). Where it reaches the silhouette the body there
 * is not paint a part can stand or be painted on: the coupe's gloves stood on
 * its louvred hood slots, and a cape must end where the painted tail ends.
 */

// A name PART, not any substring: "Trim_barrel_FL" and "Chrome_disc_RR" are
// wheel parts ("_" is a word character, so the M128c `\b` never fired after
// it and barrels, calipers and mirror glass got measured), while "Trim" must
// not read as "rim".
const SKIP = /(?:^|[^a-z])(tyre|tire|rim|wheel|barrel|caliper|disc|axle|shock|spring|driveshaft|diff|mirror|spike|link|coil|bead|lipring)/i;

/** True for a part the surface never measures (exported for the tests). */
export function skipsPart(name: string): boolean {
  return SKIP.test(name);
}

/**
 * A regular grid over two car-local axes (a, b) holding the largest value
 * seen per cell (NaN = nothing there). Reads bilinearly between cell centres.
 */
export class Grid2 {
  constructor(
    readonly a0: number,
    readonly b0: number,
    readonly da: number,
    readonly db: number,
    readonly na: number,
    readonly nb: number,
    /** Row-major [ib][ia]; NaN = no geometry in that cell. */
    readonly data: Float32Array,
  ) {}

  private cell(ia: number, ib: number): number {
    if (ia < 0 || ib < 0 || ia >= this.na || ib >= this.nb) return NaN;
    return this.data[ib * this.na + ia]!;
  }

  /** The value over (a, b), bilinear between cell centres; null where nothing
   *  was measured. A partly-empty neighbourhood answers with its highest known
   *  cell (a shelf edge, never a dip). */
  at(a: number, b: number): number | null {
    const fa = (a - this.a0) / this.da - 0.5;
    const fb = (b - this.b0) / this.db - 0.5;
    const ia = Math.floor(fa);
    const ib = Math.floor(fb);
    const ta = fa - ia;
    const tb = fb - ib;
    const c00 = this.cell(ia, ib);
    const c10 = this.cell(ia + 1, ib);
    const c01 = this.cell(ia, ib + 1);
    const c11 = this.cell(ia + 1, ib + 1);
    let known = 0;
    let best = -Infinity;
    for (const c of [c00, c10, c01, c11]) {
      if (Number.isNaN(c)) continue;
      known++;
      if (c > best) best = c;
    }
    if (known === 0) return null;
    if (known < 4) return best;
    return (c00 * (1 - ta) + c10 * ta) * (1 - tb) + (c01 * (1 - ta) + c11 * ta) * tb;
  }

  /** The largest value in any cell overlapping [a0, a1] × [b0, b1]; null when
   *  every such cell is empty. */
  maxIn(a0: number, a1: number, b0: number, b1: number): number | null {
    const i0 = Math.max(0, Math.floor((Math.min(a0, a1) - this.a0) / this.da));
    const i1 = Math.min(this.na - 1, Math.floor((Math.max(a0, a1) - this.a0) / this.da));
    const j0 = Math.max(0, Math.floor((Math.min(b0, b1) - this.b0) / this.db));
    const j1 = Math.min(this.nb - 1, Math.floor((Math.max(b0, b1) - this.b0) / this.db));
    let best = -Infinity;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = this.data[j * this.na + i]!;
        if (!Number.isNaN(v) && v > best) best = v;
      }
    }
    return best === -Infinity ? null : best;
  }

  /** The grid with every measured cell raised to the largest value within
   *  `ra` × `rb` (units) of it: the ENVELOPE a sheet lies on. A cloth vertex
   *  every few centimetres sampling this can never let a ridge between two
   *  vertices (a louvre, a fin, a wing blade) poke through their triangle. */
  dilate(ra: number, rb: number): Grid2 {
    const ka = Math.max(0, Math.ceil(ra / this.da));
    const kb = Math.max(0, Math.ceil(rb / this.db));
    const out = new Float32Array(this.data.length).fill(NaN);
    for (let ib = 0; ib < this.nb; ib++) {
      for (let ia = 0; ia < this.na; ia++) {
        if (Number.isNaN(this.data[ib * this.na + ia]!)) continue; // the footprint keeps its shape
        let best = -Infinity;
        for (let jb = Math.max(0, ib - kb); jb <= Math.min(this.nb - 1, ib + kb); jb++) {
          for (let ja = Math.max(0, ia - ka); ja <= Math.min(this.na - 1, ia + ka); ja++) {
            const v = this.data[jb * this.na + ja]!;
            if (!Number.isNaN(v) && v > best) best = v;
          }
        }
        out[ib * this.na + ia] = best;
      }
    }
    return new Grid2(this.a0, this.b0, this.da, this.db, this.na, this.nb, out);
  }
}

interface Grids {
  /** Over (x, z): the highest y. */
  top: Grid2;
  /** Over (x, y): how far BACK the body reaches, stored as -z (largest =
   *  rearmost) so the shared max/dilate logic applies. */
  rear: Grid2;
  /** Over (z, y): the outermost |x|. */
  side: Grid2;
  /** M154: the same three views holding only NON-paint points (NaN = none). */
  openTop: Grid2;
  openRear: Grid2;
  openSide: Grid2;
  /** M154 r2: the rear view of the LAMPS alone (tail and head lamps, lenses,
   *  LED lines, amber markers; NaN = none): a cape's hem never cuts across one. */
  lampRear: Grid2;
}

/** The ride's measured shape: the three silhouettes, computed on first read. */
export class SurfaceField {
  private grids: Grids | null = null;
  private source: (() => Grids) | null;

  constructor(source: Grids | (() => Grids)) {
    if (typeof source === 'function') this.source = source;
    else {
      this.grids = source;
      this.source = null;
    }
  }

  private get g(): Grids {
    if (!this.grids) {
      this.grids = this.source!();
      this.source = null;
    }
    return this.grids;
  }

  /** The top heightfield (seen from above). */
  get top(): Grid2 {
    return this.g.top;
  }

  /** The rear silhouette (over x, y; values are -z). */
  get rear(): Grid2 {
    return this.g.rear;
  }

  /** The side silhouette (over z, y; values are |x|). */
  get side(): Grid2 {
    return this.g.side;
  }

  /** The top heightfield's cells (row-major [iz][ix], NaN = empty). */
  get data(): Float32Array {
    return this.g.top.data;
  }

  /** The surface height over a car-local (x, z); null where nothing was
   *  measured. */
  topAt(x: number, z: number): number | null {
    return this.g.top.at(x, z);
  }

  /** The highest point of the body over a footprint x0..x1 × z0..z1. */
  topIn(x0: number, x1: number, z0: number, z1: number): number | null {
    return this.g.top.maxIn(x0, x1, z0, z1);
  }

  /** The rearmost z the body reaches anywhere in the box x0..x1 × y0..y1
   *  (seen from behind); null when the box is empty. */
  rearZ(x0: number, x1: number, y0: number, y1: number): number | null {
    const v = this.g.rear.maxIn(x0, x1, y0, y1);
    return v === null ? null : -v;
  }

  /** The outermost |x| the body reaches in the box z0..z1 × y0..y1 (seen
   *  from the side); null when empty. */
  sideX(z0: number, z1: number, y0: number, y1: number): number | null {
    return this.g.side.maxIn(z0, z1, y0, y1);
  }

  /** The same shape with the TOP raised to its envelope (Grid2.dilate). */
  envelope(rx: number, rz: number): SurfaceField {
    const g = this.g;
    return new SurfaceField({ ...g, top: g.top.dilate(rx, rz) });
  }

  /** M154: the side silhouette raised to its envelope (each cell to the
   *  outermost |x| within rz × ry of it): read bilinearly it never dips
   *  inside the body between two cells, so a decal laid on it is never cut. */
  sideEnvelope(rz: number, ry: number): Grid2 {
    return this.g.side.dilate(rz, ry);
  }

  /** M154: the three views of the NON-paint geometry alone (NaN = none). */
  get openTop(): Grid2 {
    return this.g.openTop;
  }

  get openRear(): Grid2 {
    return this.g.openRear;
  }

  get openSide(): Grid2 {
    return this.g.openSide;
  }

  /** M154 r2: the height runs (y0..y1, bottom up) of the LAMPS seen from
   *  behind across x0..x1 between yFloor and yTop, on the tail's face: lamp
   *  rows whose rearmost lamp point lies within `recede` of the band's
   *  rearmost point. A cape's hem must end fully above or fully below each. */
  lampRuns(x0: number, x1: number, yFloor: number, yTop: number, recede = 0.15): [number, number][] {
    const { rear, lampRear: l } = this.g;
    const i0 = Math.max(0, Math.floor((Math.min(x0, x1) - rear.a0) / rear.da));
    const i1 = Math.min(rear.na - 1, Math.floor((Math.max(x0, x1) - rear.a0) / rear.da));
    const j0 = Math.max(0, Math.floor((Math.min(yFloor, yTop) - rear.b0) / rear.db));
    const j1 = Math.min(rear.nb - 1, Math.floor((Math.max(yFloor, yTop) - rear.b0) / rear.db));
    if (i1 < i0 || j1 < j0) return [];
    let back = -Infinity;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = rear.data[j * rear.na + i]!;
        if (!Number.isNaN(v) && v > back) back = v;
      }
    }
    if (back === -Infinity) return [];
    const runs: [number, number][] = [];
    let start = -1;
    for (let j = j0; j <= j1 + 1; j++) {
      let lamp = false;
      if (j <= j1) {
        for (let i = i0; i <= i1 && !lamp; i++) {
          const v = l.data[j * l.na + i]!;
          if (!Number.isNaN(v) && v >= back - recede) lamp = true;
        }
      }
      if (lamp && start < 0) start = j;
      if (!lamp && start >= 0) {
        runs.push([rear.b0 + start * rear.db, rear.b0 + j * rear.db]);
        start = -1;
      }
    }
    return runs;
  }

  /**
   * M154 polish r2: the LAMPS the race camera sees on the tail, as a mask
   * over the rear view's cells between x0..x1 and yFloor..yTop: a cell is
   * lamp when a lamp point sits within `recede` of the body's rearmost point
   * in that cell (a cloth hanging behind the tail covers every lamp up to
   * that deep), and a row of lamps joined across dark NON-PAINT trim (the
   * monster truck's tail bar: lamps at its ends, a dark mesh between) is one
   * lamp bar. Rows run bottom up; `x(i)` / `y(j)` are the cell's low edges.
   */
  lampMask(
    x0: number,
    x1: number,
    yFloor: number,
    yTop: number,
    recede = 0.6,
    depth = 0.03,
  ): { cols: number; rows: number; mask: Uint8Array; x: (i: number) => number; y: (j: number) => number; dx: number; dy: number } | null {
    const { rear, lampRear: l, openRear: o } = this.g;
    const i0 = Math.max(0, Math.floor((Math.min(x0, x1) - rear.a0) / rear.da));
    const i1 = Math.min(rear.na - 1, Math.floor((Math.max(x0, x1) - rear.a0) / rear.da));
    const j0 = Math.max(0, Math.floor((Math.min(yFloor, yTop) - rear.b0) / rear.db));
    const j1 = Math.min(rear.nb - 1, Math.floor((Math.max(yFloor, yTop) - rear.b0) / rear.db));
    if (i1 < i0 || j1 < j0) return null;
    const cols = i1 - i0 + 1;
    const rows = j1 - j0 + 1;
    const mask = new Uint8Array(cols * rows);
    // 1 lamp, 3 dark trim joining two lamps in its row, 0 anything else
    const kind = new Uint8Array(cols * rows);
    for (let j = 0; j < rows; j++) {
      const row = new Uint8Array(cols); // 1 lamp, 2 dark trim, 0 paint / nothing
      for (let i = 0; i < cols; i++) {
        const k = (j0 + j) * rear.na + i0 + i;
        const v = rear.data[k]!;
        if (Number.isNaN(v)) continue;
        const lv = l.data[k]!;
        if (!Number.isNaN(lv) && lv >= v - recede) row[i] = 1;
        else {
          const w = o.data[k]!;
          if (!Number.isNaN(w) && w >= v - depth) row[i] = 2;
        }
      }
      // a dark run with a lamp at both ends is the bar between them
      let last = -1;
      for (let i = 0; i < cols; i++) {
        if (row[i] === 1) {
          kind[j * cols + i] = 1;
          if (last >= 0 && i - last > 1) {
            let dark = true;
            for (let q = last + 1; q < i && dark; q++) dark = row[q] === 2;
            if (dark) for (let q = last + 1; q < i; q++) kind[j * cols + q] = 3;
          }
          last = i;
        }
      }
    }
    // (a bar is a band, not a line: a joining cell counts when the row above
    // or below it joins too — a pickup's thin dark tailgate seam between its
    // C lamps' middle bars is not a lamp bar)
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const c = kind[j * cols + i]!;
        if (c === 1) mask[j * cols + i] = 1;
        else if (c === 3) {
          const up = j + 1 < rows ? kind[(j + 1) * cols + i]! : 0;
          const dn = j > 0 ? kind[(j - 1) * cols + i]! : 0;
          if (up === 3 || dn === 3) mask[j * cols + i] = 1;
        }
      }
    }
    return {
      cols,
      rows,
      mask,
      x: (i) => rear.a0 + (i0 + i) * rear.da,
      y: (j) => rear.b0 + (j0 + j) * rear.db,
      dx: rear.da,
      dy: rear.db,
    };
  }

  /** M154 polish r2: the height runs (y0..y1, bottom up) of the lamps a
   *  cloth hanging behind the tail across x0..x1 would cover (lampMask). */
  coveredLampRuns(x0: number, x1: number, yFloor: number, yTop: number, recede = 0.6): [number, number][] {
    // (the mask is taken across the whole tail, so a bar whose lit ends lie
    // outside the cloth still counts where it crosses behind the cloth)
    const lm = this.lampMask(Math.min(x0, x1) - 1.2, Math.max(x0, x1) + 1.2, yFloor, yTop, recede);
    if (!lm) return [];
    const under = (i: number): boolean => lm.x(i) + lm.dx > Math.min(x0, x1) && lm.x(i) < Math.max(x0, x1);
    const runs: [number, number][] = [];
    let start = -1;
    for (let j = 0; j <= lm.rows; j++) {
      let lamp = false;
      if (j < lm.rows) for (let i = 0; i < lm.cols && !lamp; i++) lamp = under(i) && lm.mask[j * lm.cols + i] === 1;
      if (lamp && start < 0) start = j;
      if (!lamp && start >= 0) {
        runs.push([lm.y(start), lm.y(j)]);
        start = -1;
      }
    }
    return runs;
  }

  /** M154 polish r2: half the widest gap, centred on `xc`, free of lamps
   *  between yFloor and yTop (lampMask); null when a lamp sits at xc (or no
   *  lamp is there at all: Infinity). */
  lampGapHalf(xc: number, reachX: number, yFloor: number, yTop: number, recede = 0.6): number | null {
    const lm = this.lampMask(xc - reachX - 1.2, xc + reachX + 1.2, yFloor, yTop, recede);
    if (!lm) return Infinity;
    let half = Infinity;
    for (let j = 0; j < lm.rows; j++) {
      for (let i = 0; i < lm.cols; i++) {
        if (lm.mask[j * lm.cols + i] !== 1) continue;
        const a = lm.x(i);
        const b = a + lm.dx;
        if (b < xc - reachX || a > xc + reachX) continue;
        if (a <= xc && b >= xc) return null;
        half = Math.min(half, a > xc ? a - xc : xc - b);
      }
    }
    return half;
  }

  /** M154: how far (x, z) is from the nearest OPENING on the top surface —
   *  a cell whose highest non-paint geometry (a vent's well or slats, a
   *  grille, a lamp, glass) reaches within `depth` of the body's top there —
   *  measured to the cell's nearest edge, up to `reach` (Infinity beyond, or
   *  when nothing was measured). */
  openDistance(x: number, z: number, reach: number, depth = 0.05): number {
    const { top, openTop: o } = this.g;
    const i0 = Math.max(0, Math.floor((x - reach - o.a0) / o.da));
    const i1 = Math.min(o.na - 1, Math.floor((x + reach - o.a0) / o.da));
    const j0 = Math.max(0, Math.floor((z - reach - o.b0) / o.db));
    const j1 = Math.min(o.nb - 1, Math.floor((z + reach - o.b0) / o.db));
    let best = Infinity;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = o.data[j * o.na + i]!;
        if (Number.isNaN(v)) continue;
        const t = top.data[j * top.na + i]!;
        if (!Number.isNaN(t) && v < t - depth) continue;
        const cx = Math.min(Math.max(x, o.a0 + i * o.da), o.a0 + (i + 1) * o.da);
        const cz = Math.min(Math.max(z, o.b0 + j * o.db), o.b0 + (j + 1) * o.db);
        const d = Math.hypot(cx - x, cz - z);
        if (d < best) best = d;
      }
    }
    return best <= reach ? best : Infinity;
  }

  /** M154: true when the flank seen from the side over z0..z1 × y0..y1 is
   *  PAINT a decal can lie on: every cell is measured, none sinks more than
   *  `recess` inside the patch's outermost point (a wheel arch, a cut-out),
   *  and none holds non-paint geometry (a scoop, a vent, a handle, a lamp)
   *  within `depth` of the flank. */
  flankPainted(z0: number, z1: number, y0: number, y1: number, recess = 0.08, depth = 0.025): boolean {
    const { side, openSide: o } = this.g;
    const i0 = Math.max(0, Math.floor((Math.min(z0, z1) - side.a0) / side.da));
    const i1 = Math.min(side.na - 1, Math.floor((Math.max(z0, z1) - side.a0) / side.da));
    const j0 = Math.max(0, Math.floor((Math.min(y0, y1) - side.b0) / side.db));
    const j1 = Math.min(side.nb - 1, Math.floor((Math.max(y0, y1) - side.b0) / side.db));
    if (i1 < i0 || j1 < j0) return false;
    let outer = -Infinity;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = side.data[j * side.na + i]!;
        if (Number.isNaN(v)) return false;
        if (v > outer) outer = v;
      }
    }
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = side.data[j * side.na + i]!;
        if (v < outer - recess) return false;
        const w = o.data[j * o.na + i]!;
        if (!Number.isNaN(w) && w >= v - depth) return false;
      }
    }
    return true;
  }

  /** M154 polish: true when the top seen from above over x0..x1 × z0..z1 is
   *  PAINT a decal can lie on (the Formula's sidepod tops): every cell is
   *  measured, none sinks more than `recess` under the patch's highest point,
   *  and none holds non-paint geometry (an intake, a vent, a lamp, glass)
   *  within `depth` of the top. */
  topPainted(x0: number, x1: number, z0: number, z1: number, recess = 0.08, depth = 0.03): boolean {
    const { top, openTop: o } = this.g;
    const i0 = Math.max(0, Math.floor((Math.min(x0, x1) - top.a0) / top.da));
    const i1 = Math.min(top.na - 1, Math.floor((Math.max(x0, x1) - top.a0) / top.da));
    const j0 = Math.max(0, Math.floor((Math.min(z0, z1) - top.b0) / top.db));
    const j1 = Math.min(top.nb - 1, Math.floor((Math.max(z0, z1) - top.b0) / top.db));
    if (i1 < i0 || j1 < j0) return false;
    let high = -Infinity;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = top.data[j * top.na + i]!;
        if (Number.isNaN(v)) return false;
        if (v > high) high = v;
      }
    }
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = top.data[j * top.na + i]!;
        if (v < high - recess) return false;
        const w = o.data[j * o.na + i]!;
        if (!Number.isNaN(w) && w >= v - depth) return false;
      }
    }
    return true;
  }

  /** M154: where the tail's FACE ends across x0..x1, seen from behind,
   *  between yTop and yFloor: the face = the rows whose rearmost geometry
   *  (on at least 30% of the band) lies within `recede` of the band's
   *  rearmost point. A painted bumper (rows at least 40% paint: lamps and a
   *  plate pocket break it) over a dark bottom band (a diffuser, an
   *  exhaust surround) ends the face at the bumper's lower edge — the paint
   *  band must be real, `bumper` tall or more; a dark tail PANEL under a
   *  painted lip (the muscle car's lamp recess) is still face, down to its
   *  lowest row. Null when the band holds no face. */
  tailFaceBottom(
    x0: number,
    x1: number,
    yTop: number,
    yFloor: number,
    recede = 0.15,
    bumper = 0.09,
    depth = 0.03,
    stopAtPaint = true,
  ): number | null {
    const { rear, openRear: o } = this.g;
    const i0 = Math.max(0, Math.floor((Math.min(x0, x1) - rear.a0) / rear.da));
    const i1 = Math.min(rear.na - 1, Math.floor((Math.max(x0, x1) - rear.a0) / rear.da));
    const j0 = Math.max(0, Math.floor((Math.min(yFloor, yTop) - rear.b0) / rear.db));
    const j1 = Math.min(rear.nb - 1, Math.floor((Math.max(yFloor, yTop) - rear.b0) / rear.db));
    if (i1 < i0 || j1 < j0) return null;
    let back = -Infinity; // rearmost, as -z
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = rear.data[j * rear.na + i]!;
        if (!Number.isNaN(v) && v > back) back = v;
      }
    }
    if (back === -Infinity) return null;
    const cols = i1 - i0 + 1;
    // per row (bottom up): 0 = not face, 1 = dark face, 2 = painted face
    const kind: number[] = [];
    for (let j = j0; j <= j1; j++) {
      let face = 0;
      let paint = 0;
      for (let i = i0; i <= i1; i++) {
        const v = rear.data[j * rear.na + i]!;
        if (Number.isNaN(v) || v < back - recede) continue;
        face++;
        const w = o.data[j * o.na + i]!;
        if (Number.isNaN(w) || w < v - depth) paint++;
      }
      kind.push(face < 0.3 * cols ? 0 : paint >= 0.4 * face ? 2 : 1);
    }
    const lowestFace = kind.findIndex((k) => k > 0);
    if (lowestFace < 0) return null;
    const lowestPaint = stopAtPaint ? kind.findIndex((k) => k === 2) : -1;
    if (lowestPaint >= 0) {
      let rows = 0;
      for (let r = lowestPaint; r < kind.length && kind[r] === 2; r++) rows++;
      if (rows * rear.db >= bumper - 1e-9) return rear.b0 + (j0 + lowestPaint) * rear.db;
    }
    return rear.b0 + (j0 + lowestFace) * rear.db;
  }
}

/** Accumulates car-local points into the three max-grids. */
class Binner {
  private readonly top: Float32Array;
  private readonly rear: Float32Array;
  private readonly side: Float32Array;
  private readonly oTop: Float32Array;
  private readonly oRear: Float32Array;
  private readonly oSide: Float32Array;
  private readonly lRear: Float32Array;
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;

  constructor(
    readonly b: { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number },
    readonly nx: number,
    readonly ny: number,
    readonly nz: number,
  ) {
    this.dx = Math.max(1e-4, (b.x1 - b.x0) / nx);
    this.dy = Math.max(1e-4, (b.y1 - b.y0) / ny);
    this.dz = Math.max(1e-4, (b.z1 - b.z0) / nz);
    this.top = new Float32Array(nx * nz).fill(NaN);
    this.rear = new Float32Array(nx * ny).fill(NaN);
    this.side = new Float32Array(nz * ny).fill(NaN);
    this.oTop = new Float32Array(nx * nz).fill(NaN);
    this.oRear = new Float32Array(nx * ny).fill(NaN);
    this.oSide = new Float32Array(nz * ny).fill(NaN);
    this.lRear = new Float32Array(nx * ny).fill(NaN);
  }

  /** `open` = the point belongs to a non-paint part (M154); `lamp` = to a
   *  lamp (M154 r2). */
  add(x: number, y: number, z: number, open = false, lamp = false): void {
    const ix = Math.min(this.nx - 1, Math.max(0, Math.floor((x - this.b.x0) / this.dx)));
    const iy = Math.min(this.ny - 1, Math.max(0, Math.floor((y - this.b.y0) / this.dy)));
    const iz = Math.min(this.nz - 1, Math.max(0, Math.floor((z - this.b.z0) / this.dz)));
    const ax = Math.abs(x);
    const kt = iz * this.nx + ix;
    const kr = iy * this.nx + ix;
    const ks = iy * this.nz + iz;
    max(this.top, kt, y);
    max(this.rear, kr, -z);
    max(this.side, ks, ax);
    if (open) {
      max(this.oTop, kt, y);
      max(this.oRear, kr, -z);
      max(this.oSide, ks, ax);
    }
    if (lamp) max(this.lRear, kr, -z);
  }

  finish(): Grids {
    const { b, nx, ny, nz } = this;
    return {
      top: new Grid2(b.x0, b.z0, this.dx, this.dz, nx, nz, fillHoles(this.top, nx, nz)),
      rear: new Grid2(b.x0, b.y0, this.dx, this.dy, nx, ny, fillHoles(this.rear, nx, ny)),
      side: new Grid2(b.z0, b.y0, this.dz, this.dy, nz, ny, fillHoles(this.side, nz, ny)),
      // (never hole-filled: an empty cell is simply no opening)
      openTop: new Grid2(b.x0, b.z0, this.dx, this.dz, nx, nz, this.oTop),
      openRear: new Grid2(b.x0, b.y0, this.dx, this.dy, nx, ny, this.oRear),
      openSide: new Grid2(b.z0, b.y0, this.dz, this.dy, nz, ny, this.oSide),
      lampRear: new Grid2(b.x0, b.y0, this.dx, this.dy, nx, ny, this.lRear),
    };
  }
}

function max(a: Float32Array, k: number, v: number): void {
  const cur = a[k]!;
  if (Number.isNaN(cur) || v > cur) a[k] = v;
}

/** Fill the small INTERIOR holes a sparse mesh leaves: an empty cell with
 *  measured cells on two opposite sides (within three cells) takes the higher
 *  of them, so the field reads as one solid shell (a NaN cell would drop hair
 *  through it).
 *  M153: only gaps BETWEEN measured cells — the M128c fill also grew every
 *  silhouette outward by two cells (a tail read 10 cm longer than it is). */
function fillHoles(data: Float32Array, na: number, nb: number): Float32Array {
  const REACH = 3;
  const PAIRS: [number, number][] = [
    [1, 0],
    [0, 1],
    [1, 1],
    [1, -1],
  ];
  for (let pass = 0; pass < 2; pass++) {
    const src = data.slice();
    const at = (i: number, j: number): number => (i < 0 || j < 0 || i >= na || j >= nb ? NaN : src[j * na + i]!);
    for (let j = 0; j < nb; j++) {
      for (let i = 0; i < na; i++) {
        const k = j * na + i;
        if (!Number.isNaN(src[k]!)) continue;
        let best = NaN;
        for (const [di, dj] of PAIRS) {
          // the nearest measured cell up to REACH away on each side
          let p = NaN;
          let q = NaN;
          for (let r = 1; r <= REACH && (Number.isNaN(p) || Number.isNaN(q)); r++) {
            if (Number.isNaN(p)) p = at(i + di * r, j + dj * r);
            if (Number.isNaN(q)) q = at(i - di * r, j - dj * r);
          }
          if (Number.isNaN(p) || Number.isNaN(q)) continue;
          const v = Math.max(p, q);
          if (Number.isNaN(best) || v > best) best = v;
        }
        if (!Number.isNaN(best)) data[k] = best;
      }
    }
  }
  return data;
}

/** Vertical cells of about 3 cm. */
function rowsFor(y0: number, y1: number): number {
  return Math.max(4, Math.min(80, Math.round((y1 - y0) / 0.03)));
}

/**
 * Measure `root` (a fitted vehicle model) in the coordinates of `frame` (the
 * car-local group the mod rig hangs from — `frame` must be an ancestor of the
 * meshes). The part placement is captured now; the faces are rasterised the
 * first time the field is read. Null when there is nothing to measure.
 */
export function surfaceFromObject(root: THREE.Object3D, frame: THREE.Object3D, nx = 32, nz = 72): SurfaceField | null {
  const parts: Part[] = [];
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (SKIP.test(mesh.name) || SKIP.test(mesh.parent?.name ?? '')) return;
    const pos = mesh.geometry.getAttribute('position');
    if (!pos || pos.count < 3) return;
    // The chain of LOCAL matrices from the mesh up to the frame.
    const rel = new THREE.Matrix4();
    let p: THREE.Object3D | null = mesh;
    let found = false;
    while (p) {
      if (p === frame) {
        found = true;
        break;
      }
      if (p.matrixAutoUpdate) p.updateMatrix();
      rel.premultiply(p.matrix);
      p = p.parent;
    }
    // (classified now: carPartOf remembers the GLB's own material name even
    // after a re-dress replaced the material)
    if (found) {
      const part = carPartOf(mesh);
      parts.push({ pos, index: mesh.geometry.index, m: rel, open: part !== 'paint', lamp: LAMP_PARTS.has(part) });
    }
  });
  if (parts.length === 0) return null;
  let verts = 0;
  for (const pt of parts) verts += pt.pos.count;
  if (verts < 10) return null;
  return new SurfaceField(() => rasterise(parts, nx, nz));
}

interface Part {
  pos: THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
  index: THREE.BufferAttribute | null;
  m: THREE.Matrix4;
  /** A non-paint part (M154: the `open` layer). */
  open: boolean;
  /** A lamp (M154 r2: the `lamp` layer). */
  lamp: boolean;
}

/** M154 r2: the lamp classes (carLook.carPartOf): a cape's hem keeps off them. */
const LAMP_PARTS: ReadonlySet<string> = new Set(['tail', 'head', 'lens', 'ledred', 'ledcool', 'amber']);

/** Rasterise every triangle (a point at least every `STEP` units) into the
 *  grids. */
function rasterise(parts: Part[], nx: number, nz: number): Grids {
  const STEP = 0.03;
  const world: Float32Array[] = [];
  const v = new THREE.Vector3();
  const b = { x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity, z0: Infinity, z1: -Infinity };
  for (const pt of parts) {
    const w = new Float32Array(pt.pos.count * 3);
    for (let i = 0; i < pt.pos.count; i++) {
      v.fromBufferAttribute(pt.pos, i).applyMatrix4(pt.m);
      w[i * 3] = v.x;
      w[i * 3 + 1] = v.y;
      w[i * 3 + 2] = v.z;
      if (v.x < b.x0) b.x0 = v.x;
      if (v.x > b.x1) b.x1 = v.x;
      if (v.y < b.y0) b.y0 = v.y;
      if (v.y > b.y1) b.y1 = v.y;
      if (v.z < b.z0) b.z0 = v.z;
      if (v.z > b.z1) b.z1 = v.z;
    }
    world.push(w);
  }
  const bin = new Binner(b, nx, rowsFor(b.y0, b.y1), nz);
  parts.forEach((pt, pi) => {
    const w = world[pi]!;
    const n = pt.index ? pt.index.count : pt.pos.count;
    const idx = (i: number): number => (pt.index ? pt.index.getX(i) : i);
    for (let t = 0; t + 2 < n; t += 3) {
      const a = idx(t) * 3;
      const c = idx(t + 1) * 3;
      const d = idx(t + 2) * 3;
      const ax = w[a]!, ay = w[a + 1]!, az = w[a + 2]!;
      const bx = w[c]!, by = w[c + 1]!, bz = w[c + 2]!;
      const cx = w[d]!, cy = w[d + 1]!, cz = w[d + 2]!;
      const e = Math.max(
        Math.hypot(bx - ax, by - ay, bz - az),
        Math.hypot(cx - bx, cy - by, cz - bz),
        Math.hypot(ax - cx, ay - cy, az - cz),
      );
      const k = Math.min(40, Math.max(1, Math.ceil(e / STEP)));
      for (let i = 0; i <= k; i++) {
        for (let j = 0; j <= k - i; j++) {
          const u = i / k;
          const s = j / k;
          const r = 1 - u - s;
          bin.add(ax * r + bx * u + cx * s, ay * r + by * u + cy * s, az * r + bz * u + cz * s, pt.open, pt.lamp);
        }
      }
    }
  });
  return bin.finish();
}

/** Bin car-local points (x, y, z triples) into the three silhouettes. Pure;
 *  points only (no faces) — the tests' and probes' entry. `openPts` are
 *  points of non-paint parts (M154: a vent's well, a grille): they shape the
 *  silhouettes too and mark the `open` layer; `lampPts` (M154 r2) are lamp
 *  points: open, and the `lamp` layer too. */
export function surfaceFromPoints(
  pts: readonly number[],
  nx = 24,
  nz = 60,
  openPts: readonly number[] = [],
  lampPts: readonly number[] = [],
): SurfaceField | null {
  if (pts.length < 30 || nx < 1 || nz < 1) return null; // ten points or nothing to measure
  const b = { x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity, z0: Infinity, z1: -Infinity };
  const all = openPts.length || lampPts.length ? [...pts, ...openPts, ...lampPts] : pts;
  for (let i = 0; i < all.length; i += 3) {
    const x = all[i]!;
    const y = all[i + 1]!;
    const z = all[i + 2]!;
    if (x < b.x0) b.x0 = x;
    if (x > b.x1) b.x1 = x;
    if (y < b.y0) b.y0 = y;
    if (y > b.y1) b.y1 = y;
    if (z < b.z0) b.z0 = z;
    if (z > b.z1) b.z1 = z;
  }
  const bin = new Binner(b, nx, rowsFor(b.y0, b.y1), nz);
  for (let i = 0; i < pts.length; i += 3) bin.add(pts[i]!, pts[i + 1]!, pts[i + 2]!);
  for (let i = 0; i < openPts.length; i += 3) bin.add(openPts[i]!, openPts[i + 1]!, openPts[i + 2]!, true);
  for (let i = 0; i < lampPts.length; i += 3) bin.add(lampPts[i]!, lampPts[i + 1]!, lampPts[i + 2]!, true, true);
  return new SurfaceField(bin.finish());
}
